'use client';

import { useEffect, useRef } from 'react';
import styles from './ParallaxGlowOrbs.module.css';

interface ParallaxGlowOrbsProps {
  topLeftColor?: string;
  bottomRightColor?: string;
  intensity?: number;
  /** Degrees of rotation per 1000px of scroll (default: 45) */
  rotationSpeed?: number;
}

export function ParallaxGlowOrbs({
  topLeftColor = 'rgba(0, 255, 167, 0.15)',
  bottomRightColor = 'rgba(0, 255, 211, 0.15)',
  intensity = 60,
  rotationSpeed = 45,
}: ParallaxGlowOrbsProps) {
  const orbitRef = useRef<HTMLDivElement>(null);
  const size = `${intensity * 10}px`;

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const handleScroll = () => {
      if (!orbitRef.current) return;

      // Calculate rotation angle based on scroll position
      const scrollY = window.scrollY;
      const angle = (scrollY / 1000) * rotationSpeed;

      // Update CSS custom property for smooth rotation
      orbitRef.current.style.setProperty('--scroll-angle', `${angle}deg`);
    };

    // Initial calculation
    handleScroll();

    // Use passive listener for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [rotationSpeed]);

  return (
    <div className={styles.container} aria-hidden="true">
      {/* Orbit wrapper - both orbs rotate together around viewport center */}
      <div ref={orbitRef} className={styles.orbitWrapper}>
        {/* Top left orb */}
        <div
          className={`${styles.orb} ${styles.orbTopLeft}`}
          style={{
            width: size,
            height: size,
            background: `radial-gradient(circle, ${topLeftColor} 0%, transparent 70%)`,
          }}
        />
        {/* Bottom right orb */}
        <div
          className={`${styles.orb} ${styles.orbBottomRight}`}
          style={{
            width: size,
            height: size,
            background: `radial-gradient(circle, ${bottomRightColor} 0%, transparent 70%)`,
          }}
        />
      </div>
    </div>
  );
}
